// ---------------------------------------------------------------------------
// createEmptyProject — POST to platform.cogo.xyz /api/projects to create a
// blank project (no template). Mirrors the save-design-system-library.ts and
// create-project-from-template.ts auth pattern.
// ---------------------------------------------------------------------------

import { getStudioAccessToken } from "@/services/auth-provider";
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaderRecord,
} from "@/services/cogo-platform-config";
import { saveProjectMeta } from "@/services/project-api";
import {
  withStudioProjectPlanSettings,
  type StudioProjectPlan,
} from "./studio-project-plan";

export interface CreateEmptyProjectParams {
  name: string;
  designSystemPresetId?: string | null;
  description?: string;
  appTarget?: "web" | "mobile" | "both";
  studioProjectPlan?: StudioProjectPlan;
}

export interface CreateEmptyProjectResult {
  success: boolean;
  projectId?: string;
  error?: string;
}

async function getJwt(): Promise<string | null> {
  return getStudioAccessToken();
}

export async function createEmptyProject(
  params: CreateEmptyProjectParams,
): Promise<CreateEmptyProjectResult> {
  const jwt = await getJwt();
  if (!jwt) return { success: false, error: "Sign in required" };

  try {
    const res = await fetch(cogoPlatformApiUrl("/api/projects"), {
      method: "POST",
      headers: withCogoDevProxyHeaderRecord({
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        name: params.name,
        description: params.description ?? "",
        design_system_preset_id: params.designSystemPresetId ?? null,
        app_target: params.appTarget ?? "web",
        settings: withStudioProjectPlanSettings({}, params.studioProjectPlan),
      }),
    });

    const json = (await res.json().catch(() => ({}))) as {
      id?: string;
      error?: string;
    };

    if (!res.ok) {
      return { success: false, error: json.error ?? `HTTP ${res.status}` };
    }
    if (!json.id) {
      return { success: false, error: "Malformed response (missing id)" };
    }
    if (params.studioProjectPlan) {
      await saveProjectMeta(json.id, {
        settings: withStudioProjectPlanSettings({}, params.studioProjectPlan),
      });
    }
    return { success: true, projectId: json.id };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : "Network error",
    };
  }
}
